import React, { useContext, useEffect, useReducer } from 'react';
import {
  Link, NavLink, Redirect, Route, useParams,
} from 'react-router-dom';
import { Card } from '@mui/material';
import { ModuleNav, Title } from 'shared/components/abstract/interactables';
import { searchTickets, ticketViewCheck } from 'shared/api/support';
import { checkPermissions } from 'shared/api/auth';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { useNotification, useRequest } from 'shared/hooks';
import { getBuildingPath, getUnitPath } from 'shared/routes/building';
import { getTenantPath } from 'shared/routes/tenant';
import Badge from '@mui/material/Badge';
import { truncateString } from 'shared/utils';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { CardTab } from 'shared/components/styled/tabs';
import { getTicketPath, viewPath } from 'shared/routes/support';

const initialState = {
  editEnabled: false,
  triggerReload: 0,
  ...initialDataReducerState,
};

function ticketReducer(state, { action, payload }) {
  switch (action) {
    case 'state':
      return { ...state, ...payload };
    case 'addData':
      return {
        ...state,
        data: { ...state.data, [payload.name]: payload.value },
        updatedData: {},
        dirty: false,
      };
    case 'reload':
      return { ...state, triggerReload: state.triggerReload + 1 };
    case 'updateArray': {
      // Payload is expected to be an array in this case.
      // E.G. dispatch(
      // { action: 'append', payload: { key: 'ticket_tags', value: [{id: 7, name:'satellite'] } });
      const { key, value: newValue } = payload;
      const { data, updatedData } = state;
      const newData = { ...updatedData };

      if (newValue === data[key]) {
        delete newData[key];
      }
      if (newData[key] !== newValue) {
        newData[key] = newValue;
      }

      return { ...state, updatedData: newData, dirty: Object.keys(newData).length > 0 };
    }
    default:
      return dataReducer(state, {
        action,
        payload,
      });
  }
}

/**
 * @param {Object} props
 * @param {Array<Object>} props.enabledRoutes
 * @param {Object} props.location
 */
export default function TicketNav({ enabledRoutes, location }) {
  const { request } = useRequest();
  const [ticketState, ticketDispatch] = useReducer(ticketReducer, initialState);
  const syncSideNav = useContext(SideNavContext);
  const notification = useNotification();
  const { routeId } = useParams();
  const {
    data: ticket,
    triggerReload,
  } = ticketState;

  useEffect(() => {
    (async () => {
      const response = await request(checkPermissions([
        ticketViewCheck,
      ]));
      if (response) {
        ticketDispatch({
          action: 'state',
          payload: {
            editEnabled: response[0],
          },
        });
      }
    })();
  }, [request]);

  useEffect(() => {
    (async () => {
      const ticketResponse = await request(searchTickets(`?ticket_id=${routeId}&expand_all=true`));
      if (!ticketResponse || ticketResponse.rows.length === 0) {
        ticketDispatch({ action: DataReducerActions.set, payload: null });
        return;
      }
      const [t] = ticketResponse.rows;
      const { assignee_username: assignee, assignee_id: assigneeId } = t.ticket_assignment;
      ticketDispatch({
        action: DataReducerActions.set,
        payload: { ...t, assignee, assignee_id: assigneeId },
      });
    })();
  }, [routeId, request, triggerReload]);

  useEffect(() => {
    if (ticket) {
      syncSideNav(
        getTicketPath(ticket.id),
        `${truncateString(ticket.title, 42)}[#${ticket.id}]`,
      );
    }
  }, [ticket, syncSideNav]);

  if (ticket === undefined) return null;
  if (ticket === null) {
    notification.pushError(`Unable to access Ticket #${routeId}`);
    return <Redirect to={viewPath} />;
  }

  const {
    ticket_association: {
      building,
      tenant,
      unit,
    },
  } = ticket;

  const links = [];
  const routes = [];

  enabledRoutes.forEach(({
    getPath,
    RouteComponent,
    exact,
    text,
  }) => {
    const path = getPath(routeId);

    let receiverCount = 0;
    if (ticket?.ticket_association.tenant?.dtv_receivers != null) {
      for (let i = 1; i < 7; i += 1) {
        if (ticket?.ticket_association.tenant?.dtv_receivers[`r${i}_model`] !== '') {
          receiverCount += 1;
        }
      }
    }

    switch (text) {
      case 'Files':
        links.push((
          <CardTab
            key={path}
            label={<Badge key={path} badgeContent={ticket?.files?.length} overlap="circular">{text}</Badge>}
            value={path}
            component={NavLink}
            to={path}
            id={`simple-tabpanel-${text}`}
            aria-controls={`simple-tabpanel-${text}`}
          />
        ));
        break;
      case 'Receivers':
        if (tenant) {
          links.push((
            <CardTab
              key={path}
              label={<Badge key={path} badgeContent={receiverCount} overlap="circular">{text}</Badge>}
              value={path}
              component={NavLink}
              to={path}
              id={`simple-tabpanel-${text}`}
              aria-controls={`simple-tabpanel-${text}`}
            />
          ));
        }
        break;
      default:
        links.push(
          <CardTab
            key={path}
            label={text}
            value={path}
            component={NavLink}
            to={path}
            id={`simple-tabpanel-${text}`}
            aria-controls={`simple-tabpanel-${text}`}
          />,
        );
    }

    routes.push((
      <Route
        key={path}
        path={path}
        exact={exact}
        render={() => (
          <RouteComponent
            ticketDispatch={ticketDispatch}
            ticketState={ticketState}
          />
        )}
      />
    ));
  });

  function targetTitleString() {
    let titleString = '';
    if (tenant) {
      titleString += `${tenant.first_name} ${tenant.last_name} | `;
    }
    if (unit) {
      titleString += `Unit ${unit.name} | `;
    }
    if (building) {
      titleString += `${building.name} | `;
    }
    titleString += `#${ticket.id}`;
    return titleString;
  }

  function targetTitleObject() {
    return (
      <>
        {tenant && (
          <>
            <Link to={getTenantPath(tenant.id)}>
              {`${tenant.first_name} ${tenant.last_name}`}
            </Link>
            {' | '}
          </>
        )}
        {unit && (
          <>
            <Link to={getUnitPath(building?.id, unit?.id)}>
              {`Unit ${unit.name}`}
            </Link>
            {' | '}
          </>
        )}
        {building && <Link to={getBuildingPath(building.id)}>{building.name}</Link> }
        {` - Ticket ${ticket.id}: ${truncateString(ticket.title, 128)}`}
      </>
    );
  }

  return (
    <>
      <Title title={targetTitleString()} titleObject={targetTitleObject()} />
      <Card>
        <ModuleNav links={links} value={location.pathname} label="TicketNav" />
        {routes}
      </Card>
    </>
  );
}
